import { GuideScreenshot } from '@/components/guide-screenshot';

export const meta = {
  title: 'WordCraft 在线使用指南：从草稿到排版、保存与交付',
  description:
    '在浏览器里完成一份真正可交付的文档，逐步学习提纲、正文、标题样式、表格、页面布局、审阅和保存验证。',
  updated_at: '2026-11-05',
  guide: true,
  image: '/imgs/wordcraft/design',
  imageAlt: 'WordCraft 设计选项卡与文档样式',
  category: '在线写作流程',
  readingMinutes: 10,
  toc: [
    { id: 'prepare', title: '准备写作环境' },
    { id: 'outline', title: '先列出清楚的提纲' },
    { id: 'draft', title: '写出有用的初稿' },
    { id: 'format', title: '围绕阅读体验排版' },
    { id: 'layout', title: '安排表格、图片与页面' },
    { id: 'review', title: '交付前分轮审阅' },
    { id: 'save', title: '保存并验证结果' },
    { id: 'questions', title: '在线使用常见问题' },
  ],
};

WordCraft 在线版适合希望通过浏览器处理文档、暂时不安装桌面软件的场景。要得到可靠结果，最好把一次编辑当成完整流程：先准备保存位置，再起草、排版、检查，最后确认有一份能重新打开的文件。到达编辑界面只是其中一步。

下面以一份社区活动提案为例，从零散想法整理到交付副本。你也可以换成会议简报、课程报告或客户进度说明。准备好后打开[在线编辑器页面](/online)，或者从[首页](/)进入。遇到加载、字体和保存位置问题时，可以同时参考[网页版排障指南](/wordcraft-web-version-guide)。

<h2 id="prepare">写作前先准备好环境</h2>

选择桌面或笔记本电脑，让窗口能同时容纳功能区与文档页面。持续写作时，实体键盘通常更方便。首次打开需要联网加载应用和字体，等待启动完成后，先点击编辑区域，再使用快捷键。

本站托管的在线版本基于 WordCraft v0.7.0，上游发布可能更新。因此，其他版本的截图和说明不一定与当前界面完全相同。[官方项目](https://github.com/storytold/wordcraft)可用于了解开发进度；本文重点是完成文档的工作方法，不把不同版本的所有行为视为一致。

在电脑上建一个目标文件夹，并提前决定名称，例如 `community-garden-proposal-draft.docx`。编辑现有文件时，先复制一份再打开，保留原件。文件名明确，之后才能分清今天修改的版本与之前下载的副本。

正式写作前，输入一句测试文字，保存并重新打开文件。这个简短检查验证了最关键的能力：能否把工作成果从浏览器带出来。如果连小文件都不能完成保存，应先解决这个问题，再投入长时间编辑。

<GuideScreenshot
  base="/imgs/wordcraft/backstage"
  alt="WordCraft 文件菜单中的新建、打开、保存、另存为和导出入口"
  caption="图 1：文件页面集中提供文档创建和文件操作。长时间在线写作前，先用一个小练习文件验证保存。图片来源：ArtCraft / WordCraft。"
/>

<h2 id="outline">根据读者要作出的决定列提纲</h2>

假设你希望社区组织批准一次园艺活动。读者需要知道目的、活动内容、资源需求和需要批准的事项，因此可以设置四个章节：“活动目的”“现场安排”“所需资源”“待确认事项”。

把章节名分别输入成独立段落，再在每个标题下面写一句话，说明这一节需要回答什么。例如待确认事项可以写成：“请在周五之前确认场地，并批准材料预算。”这样，提案就有明确的行动目标，读者不用猜测你期待什么回应。

提纲应短到可以一眼看清。如果两个章节都在回答同一个问题，可以合并；如果一个章节混杂背景介绍和行动要求，则考虑拆开。此时调整顺序很容易，等所有页面都排版完成后再移动，成本会高得多。

提纲也能防止无目的地增加内容。申请两小时活动，不一定需要介绍完整的社区园艺历史。先提供作出决定所必需的信息，补充背景只有确实帮助理解时才加入。

<h2 id="draft">先把初稿写清楚，再美化页面</h2>

把每个提纲句扩展为一小段。第一句先说明主要观点，然后补充支撑细节。在活动目的中解释参与者能获得什么；在现场安排中写清环节顺序；在资源部分列明需要什么、由谁准备。

尽量用具体表达。例如“参与者完成两盘育苗，并带走一份浇水计划”，比“全面提升绿色生活参与感”更容易判断。前者能帮助读者估算时间、材料和活动结果。

尚未确认的信息可以用明显标记保留，例如“[待确认场地]”，但发送前必须处理。不要为了让草稿看起来完整，就填入未经确认的日期或费用。确实需要估算时，明确写出它是估算，并解释哪些条件会使数字变化。

较长的内容可以逐节完成，并在完成一个章节或一轮重要修改后保存。保存间隔不是唯一重点，关键是记住：一个仍然开着的浏览器标签页，不等于一份已经验证的文件副本。

初稿写完后，只读每段第一句话，看看它们能否连成清楚的叙述。如果不能，优先修改段落顺序和中心句，而不是继续往后补更多文字。

<h2 id="format">围绕阅读顺序应用样式</h2>

给文档名称应用标题样式，给四个主要章节应用一级标题。现场安排需要进一步拆分时，再使用二级标题。普通解释段落保持统一正文样式。如果还不熟悉样式和层级，可以先看 [App 入门指南](/wordcraft-app-guide)。

短提案通常只需要少量视觉决定：一种正文字体、一套标题形式和适度的强调色。加粗用于突出截止时间或需要批准的项目；如果每一段几乎都加粗，强调就失去了作用。

通过段落间距控制留白，避免重复敲空行。按顺序执行的步骤用编号列表，不要求顺序的物品用项目符号列表。只有两行确实属于同一段时，才考虑手动换行。

<GuideScreenshot
  base="/imgs/wordcraft/design"
  alt="WordCraft 设计选项卡，显示主题、样式集和段落间距相关控件"
  caption="图 2：少量而一致的格式选择更容易维护。上游设计界面展示了文档层面的排版工具。图片来源：ArtCraft / WordCraft。"
/>

从外部粘贴文字后立即查看格式。突然变化的字体、过小字号和异常间距，可能与粘贴时携带的样式有关。先把新文字统一到自己的文档样式，再继续写作，比完成几页后集中清理更省力。

不必让页面严格复制某张截图。你的内容长度、字体和纸型不同，布局自然会不同。目标应是层次清楚、重点容易找到、段落之间有合适间隔，并最终检查真实输出。

<h2 id="layout">让表格和图片真正帮助理解</h2>

资源部分可以建立“物品”“数量”“负责人”三列表格，填写育苗盘、培养土和打印说明等内容。这里适合表格，是因为每一行都在描述同一种结构的信息；活动背景的长篇解释则更适合普通段落。

控制列数。如果每个单元格都换成五六行，说明信息可能装得过多。可以缩短名称，把解释移到表格下面，或拆分为两张小表。检查最长的一项，不要只凭最短的一行判断表格是否整齐。

只有图片比文字更有效时才加入。集合地点的简单平面图可以减少迷路，而与内容无关的装饰照片可能只是增加体积。选择有权使用的图片，并添加说明，告诉读者它与当前任务的关系。

<GuideScreenshot
  base="/imgs/wordcraft/layout"
  alt="WordCraft 文档页面中图片与周围文字的环绕关系"
  caption="图 3：图片会影响周围段落和分页。修改附近文字后检查一次，保存后再检查一次。图片来源：ArtCraft / WordCraft。"
/>

完成前确认纸张大小和方向，并逐页检查页末是否留下孤立标题、被拆开的表格或异常大块空白。标题单独落在页底时，应查看段落和分页相关设置，不要靠连续空行把它向下推。好的修复应该能够承受下一次文字修改。

字体问题会改变行长和页数。如果浏览器里的字体与预期不同，先参考[网页版指南](/wordcraft-web-version-guide)处理字体，再微调页面；顺序相反往往会导致重复劳动。

<h2 id="review">交付前进行三轮简短检查</h2>

第一轮核对事实：姓名、日期、数量、地点与联系信息。第二轮检查阅读顺序，删除重复内容。第三轮查看格式和页面。把这几轮分开，可以减少“看着很整齐，因此没有发现日期写错”的情况。

假设读者没有听过你的口头解释，重新读一遍。他能否理解提案内容、资源需求和需要作出的决定？如果文档可能稍后才被阅读，把“下周”改成明确日期，避免语境变化后产生误解。

需要别人反馈时，先约定返回方式：在 PDF 上批注、修改可编辑副本，或发送一份修改清单。应用运行在网页里，不代表它自动提供共享云文档或多人实时协作。选择已经验证过的交接方法。

使用批注和修订进行重要协作之前，检查保存文件在收件人软件里的实际表现。[DOCX 兼容性指南](/wordcraft-guide)提供了可重复的往返测试。简单审批场景中，一份整洁 PDF 加上明确的回复要求，可能就足够。

<h2 id="save">保存可编辑主文件，再验证交付副本</h2>

先保存可编辑文档。出现文件选择窗口时完成操作；如果浏览器直接下载，则在下载列表里找到对应文件，并移动到预先建立的文件夹。熟悉的文件名并不能证明原文件已经被原地更新。

使用能表达状态的命名，例如 `community-garden-proposal-review.docx`，尽量避免“最终版-最终-2”这种难以区分的名称。如果按日期保留修订，日期格式保持一致，让最近批准的版本容易找到。

对方需要固定阅读副本时，通过当前文件菜单导出 PDF。单独打开它，检查页数、标题位置、表格、图注和最后一段。链接重要就点击验证，需要搜索和复制文字就尝试选中一段，确保输出满足实际需求。

最后从目标文件夹重新打开 DOCX，检查本次编辑末尾修改过的一句话。这能帮助确认你看到的是当前版本，而不是早期副本。所有检查完成前先保留编辑器，确认结果存在于标签页之外，再结束会话。

<h2 id="questions">在线使用常见问题</h2>

### 打开编辑器之前必须登录吗？

本站托管的在线编辑器可以不创建网站账号就打开，从[在线页面](/online)进入即可。网站其他账号功能与打开编辑器是分开的。

### 在线版会自动把文档存到云盘吗？

不要从“运行在浏览器”推断存在云端存储或同步。请完成保存流程，并在设备上验证文件。浏览器缓存和偏好设置不能代替文档备份。

### 应该发送 DOCX 还是 PDF？

需要继续编辑时，发送经过验证的 DOCX；阅读和打印可以用 PDF。即使只发送 PDF，也应保留可编辑主文件，方便后续修改。

### 换台电脑能继续吗？

先通过自己选择的存储或分享方式转移已保存文件，再在另一台电脑打开。不要假设访问同一个网址，就会自动恢复上一台电脑的文档。

### 完成这次练习后学什么？

可以阅读[完整基础教程](/tutorials)学习具体命令，参考 [App 指南](/wordcraft-app-guide)巩固结构化写作，或回到[首页](/)选择在线与桌面使用方式。
